import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { CurrencySwitcher } from "@/components/CurrencySwitcher";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/lombok", label: "Lombok", id: "lombok" },
  { to: "/portfolio", label: "Portfolio", id: "portfolio" },
  { to: "/investor-club", label: "Investor Club", id: "investor-club" },
];

export default function Nav() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const overHero = pathname === "/" && !scrolled && !open;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          overHero
            ? "text-cream"
            : "bg-cream/85 text-pine backdrop-blur-xl border-b border-pine/10"
        }`}
        data-testid="site-nav"
      >
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-6 md:h-20 md:px-10">
          <Link to="/" className="z-10" data-testid="nav-logo">
            <span className="font-display text-xl tracking-wide md:text-2xl">
              PACHAMAMA <span className="italic text-gold">Lombok</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {LINKS.map((l) => (
              <Link
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={`group relative font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${
                  pathname === l.to
                    ? "text-terracotta"
                    : "hover:text-terracotta"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px w-full origin-left bg-terracotta transition-transform duration-300 ${
                    pathname === l.to ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                  }`}
                />
              </Link>
            ))}
            <Link
              to="/investor-club"
              data-testid="nav-cta-invest"
              className={`group flex items-center gap-1.5 rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                overHero
                  ? "border-cream/40 hover:bg-cream hover:text-ink"
                  : "border-pine/30 hover:bg-ink hover:text-cream"
              }`}
            >
              Invest with us
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <CurrencySwitcher light={overHero} />
          </nav>

          <button
            className="z-10 md:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink px-8 md:hidden"
            data-testid="nav-mobile-menu"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={l.to}
                  className="flex items-baseline gap-4 border-b border-cream/10 py-5"
                >
                  <span className="font-mono text-xs text-gold">0{i + 1}</span>
                  <span
                    className={`font-display text-4xl ${
                      pathname === l.to ? "text-gold italic" : "text-cream"
                    }`}
                  >
                    {l.label}
                  </span>
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45 }}
              className="mt-8 flex items-center gap-3"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cream/50">Currency</span>
              <CurrencySwitcher light />
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-cream/50"
            >
              <a href="mailto:info@temerariolombok.com" className="text-gold" data-testid="nav-mobile-email">
                info@temerariolombok.com
              </a>
              <br />
              <span className="mt-2 inline-block">
              8.6705° S — 116.3329° E · Lombok, Indonesia
              </span>
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
